import { ElementType } from 'react';

import { createBEMClasses } from '@/utils/classname';

import './Title.css';

const { block } = createBEMClasses('title');

export const TITLE_SIZES = ['50', '100', '200', '300', '400', '500'] as const;
export const TITLE_WEIGHTS = ['medium', 'bold'] as const;
export const TITLE_COLORS = [
  'primary',
  'secondary',
  'disabled',
  'success',
  'warning',
  'danger',
  'neutral',
] as const;

type TitleSize = (typeof TITLE_SIZES)[number];
type TitleWeight = (typeof TITLE_WEIGHTS)[number];
type TitleColor = (typeof TITLE_COLORS)[number];

interface TitleProps extends React.AriaAttributes, React.HTMLAttributes<HTMLElement> {
  size?: TitleSize;
  weight?: TitleWeight;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  color?: TitleColor;
  align?: 'left' | 'center' | 'right' | 'start' | 'end';
  textTransform?: 'capitalize' | 'uppercase' | 'lowercase' | 'none';
  whiteSpace?: 'normal' | 'nowrap' | 'pre-wrap' | 'pre-line';
  maxLines?: number;
  flex?: React.CSSProperties['flex'];
  children: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
  ellipsis?: boolean;
}

const getSemanticElement = (size: TitleSize): ElementType => {
  switch (size) {
    case '50':
      return 'h1';
    case '100':
      return 'h2';
    case '200':
      return 'h3';
    case '300':
      return 'h4';
    case '400':
      return 'h5';
    case '500':
      return 'h6';
    default:
      return 'h5';
  }
};

const Title = ({
  size = '400',
  weight = 'bold',
  as,
  color,
  align,
  textTransform,
  whiteSpace,
  maxLines,
  flex,
  children,
  className,
  style,
  id,
  ref,
  ellipsis = false,
  ...rest
}: TitleProps) => {
  const Element = as || getSemanticElement(size);

  const shouldClamp = typeof maxLines === 'number' && maxLines > 0;

  return (
    <Element
      ref={ref}
      className={block({ size, weight, ...(color && { color }), ellipsis }, className)}
      style={{
        ...style,
        textAlign: align,
        textTransform,
        whiteSpace: shouldClamp ? undefined : whiteSpace,
        flex,
        ...(shouldClamp
          ? {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: maxLines,
              overflow: 'hidden',
            }
          : {}),
      }}
      id={id}
      {...rest}
    >
      {children}
    </Element>
  );
};

export default Title;
